import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import {
  iif,
  insertItem,
  patch,
  removeItem,
  updateItem,
} from '@ngxs/store/operators';
import { ReportEntryDto } from '@osos/shared/api';
import {
  BreadcrumbToken,
  DEFAULT_PAGESIZE,
  LoadingIndicator,
  NavigationItem,
  Page,
  PermissionScopeCounts,
  ReportPresenterModel,
  Where,
} from '@osos/shared/types';
import { forkJoin, Observable, of } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { ReportsFactoryService } from '../../factories/reports-factory/reports-factory.service';
import { ApiUtilsService, BreadcrumbService } from '../../services';
import { ReportDataService } from '../../services/data-services/report-data-service/report-data.service';
import { LoadingActions } from '../loading/loading.actions';
import { ModuleState } from '../modules-data-state/module.state';
import { NavigationStateActions } from '../navigation/navigation.actions';
import { ViewActions } from '../view-data-state/view.action';
import { ReportsActions } from './reports.actions';

export interface ReportsStateModel {
  page: Page<ReportPresenterModel>;
  presentedReports: ReportPresenterModel[];
  loading: LoadingIndicator;
  presentedReport: { [key: string]: ReportPresenterModel };
  pinnedReports: ReportPresenterModel[];
  selectedReport?: string;
  counts?: PermissionScopeCounts;
  transitoryExportedReport?: Blob;
}

@State<ReportsStateModel>({
  name: 'reports',
  defaults: {
    page: {
      items: [],
      totalCount: 0,
    },
    presentedReports: [],
    pinnedReports: [],
    presentedReport: {},
    loading: new LoadingIndicator(),
    counts: {
      authenticated: 0,
      restricted: 0,
    },
  },
})
@Injectable()
export class ReportsState {
  constructor(
    private store: Store,
    private apiHelper: ApiUtilsService,
    private reportService: ReportDataService,
    private reportsFactoryService: ReportsFactoryService,
    private breadcrumbService: BreadcrumbService,
  ) {}

  //#region selectors methods

  static presentedReport(
    reportId: string,
  ): (state: ReportsStateModel) => ReportPresenterModel {
    return createSelector(
      [ReportsState],
      (state: ReportsStateModel) => state.presentedReport[reportId],
    );
  }

  @Selector([ReportsState])
  static presentedReports(state: ReportsStateModel): ReportPresenterModel[] {
    return state.presentedReports;
  }

  @Selector([ReportsState])
  static pinnedReports(state: ReportsStateModel): ReportPresenterModel[] {
    return state.pinnedReports;
  }

  @Selector([ReportsState])
  static scopesCounts(state: ReportsStateModel): PermissionScopeCounts {
    return state.counts;
  }

  @Selector([ReportsState])
  static selectedReport(state: ReportsStateModel): ReportPresenterModel {
    return state.presentedReport[state.selectedReport];
  }

  @Selector([ReportsState])
  static transitoryExportedReport(state: ReportsStateModel): Blob {
    return state.transitoryExportedReport;
  }

  //#endregion

  @Action(ReportsActions.FetchReport)
  fetchReport(
    { setState, getState }: StateContext<ReportsStateModel>,
    { payload, cache }: ReportsActions.FetchReport,
  ): Observable<ReportPresenterModel> {
    const _presentedReport = getState().presentedReport[payload];
    if (cache && !!_presentedReport) {
      return of(_presentedReport);
    }

    return this.reportService
      .findById({
        id: payload,
      })
      .pipe(
        map((report) =>
          this.reportsFactoryService.createPresenterModel(report),
        ),
        tap((report) => {
          this.breadcrumbService.set(
            BreadcrumbToken.ReportName,
            report.value.name
          );
          setState(
            patch<ReportsStateModel>({
              presentedReports: insertItem(report),
              presentedReport: patch({
                [report.id]: report,
              }),
            }),
          );

          // Resolve breadcrumb tokens:
          this.breadcrumbService.set(
            BreadcrumbToken.ReportName,
            report.value.name,
          );
        }),
      );
  }

  @Action(ReportsActions.FetchReports)
  fetchReports(
    {
      dispatch,
      setState,
      patchState,
      getState,
    }: StateContext<ReportsStateModel>,
    { payload }: ReportsActions.FetchReports,
  ) {
    const where = this._prepareWhere(payload) as Where<ReportEntryDto>;
    dispatch(new LoadingActions.SetLoading(true));
    setState(
      patch<ReportsStateModel>({
        presentedReports: payload?.keepData ? getState().presentedReports : [],
        loading: patch<LoadingIndicator>({
          page: true,
        }),
      }),
    );

    return this.reportService
      .find({
        ...this.apiHelper.prepareFilter({
          where,
          skip: payload.skip ?? 0,
          limit: payload.limit ?? DEFAULT_PAGESIZE,
        }),
      })
      .pipe(
        tap((items) => {
          patchState({
            presentedReports: items.map((report) =>
              this.reportsFactoryService.createPresenterModel(report),
            ),
          });
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetLoading(false));
          setState(
            patch<ReportsStateModel>({
              loading: patch<LoadingIndicator>({
                page: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(ReportsActions.GetCountsByTypes)
  getCountsByTypes(
    { setState }: StateContext<ReportsStateModel>,
    { payload }: ReportsActions.GetCountsByTypes,
  ): Observable<any> {
    return forkJoin([
      this.reportService.count({
        where: this._prepareCountWhere(payload, false),
      }),
      this.reportService.count({
        where: this._prepareCountWhere(payload, true),
      }),
    ]).pipe(
      tap(([authenticated, restricted]) => {
        setState(
          patch<ReportsStateModel>({
            counts: {
              authenticated: authenticated.count,
              restricted: restricted.count,
            },
          }),
        );
      }),
    );
  }

  @Action(ReportsActions.FetchPinnedReports)
  fetchPinnedReports({ patchState }: StateContext<ReportsStateModel>) {
    return this.reportService
      .find({
        filter: this.apiHelper.prepareFilter({
          where: {
            and: [{ 'value.isPublished': true }],
          },
        }),
      })
      .pipe(
        tap((reports) => {
          patchState({
            pinnedReports: reports.map((report) =>
              this.reportsFactoryService.createPresenterModel(report),
            ),
          });
        }),
      );
  }

  @Action(ReportsActions.CreateReport)
  createReport(
    { setState, dispatch }: StateContext<ReportsStateModel>,
    { payload }: ReportsActions.CreateReport,
  ) {
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<ReportsStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      }),
    );
    const _data = this.reportsFactoryService.createDataModel(payload);
    return this.reportService
      .create({
        body: _data,
      })
      .pipe(
        switchMap((res) =>
          this.reportService.findById({
            id: res.id,
          }),
        ),
      )
      .pipe(
        tap((res) => {
          const _presenter =
            this.reportsFactoryService.createPresenterModel(res);
          setState(
            patch<ReportsStateModel>({
              presentedReport: patch({
                [_presenter.id]: _presenter,
              }),
              presentedReports: insertItem(_presenter),
              page: patch<Page<ReportPresenterModel>>({
                items: insertItem(_presenter),
                totalCount: (c) => c++,
              }),
            }),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<ReportsStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(ReportsActions.UpdateReport)
  updateReport(
    { setState, getState, dispatch }: StateContext<ReportsStateModel>,
    { payload }: ReportsActions.UpdateReport,
  ): Observable<any> {
    const _data = this.reportsFactoryService.createDataModel(payload.report);
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<ReportsStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
        }),
      }),
    );
    return this.reportService
      .updateById({
        id: payload.id,
        body: _data,
      })
      .pipe(
        switchMap(() =>
          this.reportService.findById({
            id: payload.id,
          }),
        ),
        tap((res) => {
          const presenter =
            this.reportsFactoryService.createPresenterModel(res);
          const indexOfPublishedReport = getState().pinnedReports.findIndex(
            (v) => v.id === res.id,
          );
          setState(
            patch<ReportsStateModel>({
              pinnedReports: iif(
                res.value.isPublished,
                iif(
                  () => indexOfPublishedReport >= 0,
                  updateItem(indexOfPublishedReport, presenter),
                  insertItem(presenter),
                ),
                removeItem(indexOfPublishedReport),
              ),
              presentedReports: updateItem((v) => v.id === res.id, presenter),
              presentedReport: patch({
                [payload.id]: presenter,
              }),
              page: patch<Page<ReportPresenterModel>>({
                items: updateItem((v) => v.id === res.id, presenter),
              }),
            }),
          );
          // Update/Remove the report in the sidebar:
          if (presenter.value.isPublished) {
            const module = this.store.selectSnapshot(
              ModuleState.presentedModule(payload.moduleDetails.id),
            );

            const navItem: NavigationItem = {
              id: presenter.id,
              name: presenter.value.name,
              icon: presenter.value.icon,
              isPublished: presenter.value.isPublished,
              module: payload.moduleDetails.id,
              relatedTo: presenter.value.relatedTo,
              type: 'Report',
            };

            dispatch(
              new NavigationStateActions.UpsertNavigationItem(navItem, module),
            );
          } else {
            dispatch(
              new NavigationStateActions.RemoveNavigationItem(
                presenter.id,
                payload.moduleDetails.id,
              ),
            );
          }
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<ReportsStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(ReportsActions.DeleteReport)
  deleteReport(
    { setState, dispatch }: StateContext<ReportsStateModel>,
    { payload }: ReportsActions.DeleteReport,
  ): Observable<void> {
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<ReportsStateModel>({
        loading: patch<LoadingIndicator>({
          remove: true,
        }),
      }),
    );
    return this.reportService
      .deleteById({
        id: payload.reportId,
      })
      .pipe(
        tap(() => {
          setState(
            patch<ReportsStateModel>({
              page: patch<Page<ReportPresenterModel>>({
                items: removeItem((v) => v.id === payload.reportId),
              }),
              presentedReports: removeItem((v) => v.id === payload.reportId),

              pinnedReports: removeItem((v) => v.id === payload.reportId),
            }),
          );

          dispatch(
            new NavigationStateActions.RemoveNavigationItem(
              payload.reportId,
              payload.moduleDetails.id,
            ),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<ReportsStateModel>({
              loading: patch<LoadingIndicator>({
                remove: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(ReportsActions.TransitoryExportReport)
  exportReport(
    { setState, dispatch }: StateContext<ReportsStateModel>,
    { payload }: ReportsActions.TransitoryExportReport,
  ): Observable<any> {
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<ReportsStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      }),
    );
    return this.reportService
      .exportDataReport({
        id: payload.reportId,
        format: payload.format,
        selectedColumns: payload.selectedColumns,
      })
      .pipe(
        tap((res) => {
          setState(
            patch<ReportsStateModel>({
              transitoryExportedReport: res,
            }),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<ReportsStateModel>({
              loading: patch<LoadingIndicator>({
                create: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(ReportsActions.SelectReport)
  selectedReport(
    { setState, dispatch }: StateContext<ReportsStateModel>,
    { payload }: ReportsActions.SelectReport,
  ): void {
    setState(
      patch<ReportsStateModel>({
        selectedReport: payload,
      }),
    );

    // Clear any selected data-view when selecting a report:
    dispatch(new ViewActions.SelectView(null, null));
  }

  private _relatedTo(entityId): any {
    return entityId ? { 'value.relatedTo.dataEntry': entityId } : null;
  }
  private _prepareSearch(searchTerm, filterBy = ['name', 'description']): any {
    return this.apiHelper.prepareLocalizedSearch(
      searchTerm,
      filterBy ?? ['name', 'description'],
    );
  }

  private _prepareWhere(payload): any {
    return {
      ...payload.filters,
      and: [...payload.filters?.and, this._relatedTo(payload.entityId)],
      or: this._prepareSearch(payload.searchTerm),
    };
  }

  private _prepareCountWhere(payload, isRestricted = false): any {
    return this.apiHelper.prepareWhere({
      or: this._prepareSearch(payload.searchTerm),
      ...payload.filter,
      and: [
        this._relatedTo(payload.entityId),
        { 'value.authenticated.enabled': true },
        { 'value.authenticated.isRestricted': isRestricted },
        ...(payload?.filter?.and ?? ''),
      ],
    });
  }
}
